import React, { createContext, useContext, useState, useCallback } from 'react';
import ToastContainer from '@components/common/Toast/ToastContainer';

const ToastContext = createContext();

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastProvider = ({ children, position = 'bottom-right' }) => {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((toast) => {
        const id = Date.now() + Math.random();
        const newToast = {
            id,
            type: 'info',
            duration: 5000,
            ...toast,
        };

        setToasts(prev => [...prev, newToast]);
        return id;
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const clearAllToasts = useCallback(() => {
        setToasts([]);
    }, []);

    // Convenience methods
    const success = useCallback((message, options = {}) => {
        return addToast({ ...options, message, type: 'success' });
    }, [addToast]);

    const error = useCallback((message, options = {}) => {
        return addToast({ ...options, message, type: 'error' });
    }, [addToast]);

    const warning = useCallback((message, options = {}) => {
        return addToast({ ...options, message, type: 'warning' });
    }, [addToast]);

    const info = useCallback((message, options = {}) => {
        return addToast({ ...options, message, type: 'info' });
    }, [addToast]);

    const value = {
        toasts,
        addToast,
        removeToast,
        clearAllToasts,
        success,
        error,
        warning,
        info,
    };

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastContainer
                toasts={toasts}
                position={position}
                onRemove={removeToast}
            />
        </ToastContext.Provider>
    );
};